import {MutableRefObject} from 'react';

import {
  createSafeFragment,
  getCurrentSelectionRange,
  getSelectedText,
  moveCaretToEnd,
  replaceEditorHTML,
  restoreSelection,
} from './selectionHelpers';

const HEADING_TAG_NAMES = new Set(['H1', 'H2', 'H3']);
const BOLD_TAG_NAMES = new Set(['STRONG', 'B']);

export function isExplicitBold(editorNode: HTMLDivElement | null): boolean {
  if (!editorNode) {
    return false;
  }

  const selection = window.getSelection();

  if (!selection || selection.rangeCount < 1) {
    return false;
  }

  const anchorNode = selection.anchorNode;

  if (!anchorNode || !editorNode.contains(anchorNode)) {
    return false;
  }

  let currentNode: Node | null =
    anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentNode : anchorNode;

  while (currentNode && currentNode !== editorNode) {
    if (currentNode instanceof HTMLElement) {
      if (BOLD_TAG_NAMES.has(currentNode.tagName)) {
        return true;
      }

      if (HEADING_TAG_NAMES.has(currentNode.tagName)) {
        return false;
      }
    }

    currentNode = currentNode.parentNode;
  }

  return false;
}

export function queryCommandState(command: string): boolean {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

export function executeToolbarCommand(
  node: HTMLDivElement | null,
  selectionRef: MutableRefObject<Range | null>,
  command: string,
  value?: string
): void {
  if (!node) {
    return;
  }

  const savedRange = selectionRef.current;
  node.focus();
  restoreSelection(savedRange);

  const commandSucceeded = document.execCommand(command, false, value);

  if (!commandSucceeded && value) {
    if ('formatBlock' === command) {
      const tag = value.replace(/[<>]/g, '');
      insertHTML(node, selectionRef, `<${tag}>${getSelectedText() || ''}</${tag}>`);
    }
  }

  selectionRef.current = getCurrentSelectionRange(node);
}

export function insertHTML(
  node: HTMLDivElement | null,
  selectionRef: MutableRefObject<Range | null>,
  html: string
): void {
  if (!node) {
    return;
  }

  const savedRange = selectionRef.current;
  node.focus();
  restoreSelection(savedRange);

  const commandSucceeded = document.execCommand('insertHTML', false, html);

  if (!commandSucceeded) {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount < 1) {
      replaceEditorHTML(node, `${node.innerHTML}${html}`);
      moveCaretToEnd(node);
      selectionRef.current = getCurrentSelectionRange(node);
      return;
    }

    const range = selection.getRangeAt(0);
    range.deleteContents();
    const fragment = createSafeFragment(html);
    const lastChild = fragment.lastChild;
    range.insertNode(fragment);

    if (lastChild) {
      range.setStartAfter(lastChild);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  selectionRef.current = getCurrentSelectionRange(node);
}

export function insertText(
  node: HTMLDivElement | null,
  selectionRef: MutableRefObject<Range | null>,
  value: string
): void {
  if (!node) {
    return;
  }

  const savedRange = selectionRef.current;
  node.focus();
  restoreSelection(savedRange);

  const commandSucceeded = document.execCommand('insertText', false, value);

  if (!commandSucceeded) {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount < 1) {
      node.appendChild(document.createTextNode(value));
      moveCaretToEnd(node);
      selectionRef.current = getCurrentSelectionRange(node);
      return;
    }

    const range = selection.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(value);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  selectionRef.current = getCurrentSelectionRange(node);
}
